import React, { useMemo } from 'react';
import classNames from 'classnames';
import moment from 'moment';

import EventItem from './EventItem';
import useLazyLoading from 'hooks/useLazyLoading';
import { fetchOccasions } from 'services/occasion';

import Empty from 'components/common/empty/Empty';
import Loading from 'components/common/loading/Loading';

import { DEFAULT_PAGE_SIZE } from 'constants/appConstants';
import { EVENT_NOT_LOADED_TEXT, EVENT_TYPE } from 'constants/dashboard';

import { getFormattedDate } from 'utils/date';

interface FiscalYear {
  endDate?: string;
  startDate?: string;
}

interface Occasion {
  date: string;
  type: keyof typeof EVENT_TYPE;
  name: string;
}

interface EventListProps {
  currentFiscalYear: FiscalYear;
}

interface LazyLoadingResult {
  loading: boolean;
  error?: boolean;
  dataList: Occasion[];
  lastRef: (node?: Element | null) => void;
}

const EventList: React.FC<EventListProps> = ({ currentFiscalYear }) => {
  const { endDate } = currentFiscalYear;

  const fetchOccasionsForLazyLoading = async (
    params: Record<string, string | number | boolean | null | undefined>
  ): Promise<{ data?: Occasion[]; meta?: { page?: number; size?: number; total?: number } }> => {
    const response = await fetchOccasions(params);
    return {
      data: Array.isArray(response.data) ? (response.data as Occasion[]) : [],
      meta:
        typeof response.meta === 'object' && response.meta
          ? response.meta
          : { page: 1, size: DEFAULT_PAGE_SIZE, total: 0 },
    };
  };

  const filters = {
    size: DEFAULT_PAGE_SIZE,
    startDate: getFormattedDate(moment()),
    endDate: getFormattedDate(endDate),
  };

  const { loading, error, dataList, lastRef } = useLazyLoading(fetchOccasionsForLazyLoading, [
    filters,
  ]) as LazyLoadingResult;

  const events = useMemo(() => {
    const uniqueEventMap = new Map<string, Occasion>();

    dataList.forEach((event) => {
      const eventKey = `${event.date}-${event.type}-${event.name}`;
      if (uniqueEventMap.has(eventKey)) {
        return;
      }

      uniqueEventMap.set(eventKey, event);
    });

    return Array.from(uniqueEventMap.values());
  }, [dataList]);

  const isError = error;
  const isLoadingAlone = loading && dataList.length < 1;

  if (isLoadingAlone) {
    return (
      <div className="container">
        <Loading />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container mt-25">
        <div className="event__empty">
          <p>{EVENT_NOT_LOADED_TEXT}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames('event__lists custom-scroll-bar', { 'event__empty-list': events.length === 0 })}>
      {events.length === 0 ? (
        <Empty message="There are no upcoming events and holidays" />
      ) : (
        events.map((event, index) => {
          const isLastItem = index === events.length - 1;

          return (
            <EventItem
              key={`${event.date}-${event.type}-${event.name}`}
              childRef={isLastItem ? lastRef : undefined}
              date={event.date}
              eventType={event.type}
              eventTitle={event.name}
            />
          );
        })
      )}
      {loading && (
        <div className="lazy-loading">
          <Loading />
        </div>
      )}
    </div>
  );
};

export default EventList;
